/* ==========================================================================
   Aori K Beauty — global.js
   Drawers (nav + cart), AJAX cart, money formatting, scroll reveals.
   ========================================================================== */
(function () {
  'use strict';

  var Aori = window.Aori || {};
  var routes = Aori.routes || {};
  var strings = Aori.strings || {};

  /* ---------- Cart events (for wholesale/B2B & other cart apps) ---------- */
  // Apps can listen on `document` for: 'cart:updated' (any change),
  // 'cart:added' (item added), 'cart:refresh' (drawer DOM rebuilt — re-apply here).
  function emit(name, cart) {
    document.dispatchEvent(new CustomEvent(name, { detail: { cart: cart }, bubbles: true }));
  }

  /* ---------- Money formatting (Shopify money_format) -------------------- */
  function formatMoney(cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    var fmt = format || Aori.moneyFormat || '${{amount}}';
    var value = '';
    var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;

    function defaultTo(num, precision, thousands, decimal) {
      precision = isNaN(precision = Math.abs(precision)) ? 2 : precision;
      thousands = thousands === undefined ? ',' : thousands;
      decimal = decimal === undefined ? '.' : decimal;
      var number = (parseInt(cents, 10) / 100).toFixed(precision);
      var parts = number.split('.');
      var dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      var centsPart = parts[1] ? decimal + parts[1] : '';
      return dollars + centsPart;
    }

    switch (fmt.match(placeholderRegex)[1]) {
      case 'amount': value = defaultTo(2); break;
      case 'amount_no_decimals': value = defaultTo(0); break;
      case 'amount_with_comma_separator': value = defaultTo(2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = defaultTo(0, '.', ','); break;
      case 'amount_with_space_separator': value = defaultTo(2, ' ', ','); break;
      case 'amount_no_decimals_with_space_separator': value = defaultTo(0, ' '); break;
      default: value = defaultTo(2);
    }
    return fmt.replace(placeholderRegex, value);
  }
  Aori.formatMoney = formatMoney;

  /* ---------- Drawers ---------------------------------------------------- */
  var lastFocus = null;

  function openDrawer(name) {
    var drawer = document.querySelector('[data-drawer="' + name + '"]');
    if (!drawer) return;
    lastFocus = document.activeElement;
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    document.body.classList.add('no-scroll');
    var focusable = drawer.querySelector('button, a, select, input');
    if (focusable) focusable.focus();
  }

  function closeDrawer(drawer) {
    if (!drawer) drawer = document.querySelector('.drawer.is-open');
    if (!drawer) return;
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    if (!document.querySelector('.drawer.is-open')) document.body.classList.remove('no-scroll');
    if (lastFocus) lastFocus.focus();
  }

  document.addEventListener('click', function (e) {
    var opener = e.target.closest('[data-drawer-open]');
    if (opener) { e.preventDefault(); openDrawer(opener.getAttribute('data-drawer-open')); return; }
    if (e.target.closest('[data-drawer-close]')) { e.preventDefault(); closeDrawer(); }
  });

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') closeDrawer();
  });

  /* ---------- AJAX cart -------------------------------------------------- */
  function getJSON(url) { return fetch(url, { headers: { 'Accept': 'application/json' } }).then(function (r) { return r.json(); }); }

  function updateCartCount(count) {
    document.querySelectorAll('[data-cart-count]').forEach(function (el) {
      el.textContent = count;
      el.classList.toggle('is-hidden', count === 0);
    });
  }

  function refreshDrawer() {
    // Re-render the cart drawer contents from /cart.js.
    return getJSON('/cart.js').then(function (cart) {
      updateCartCount(cart.item_count);
      renderDrawer(cart);
      emit('cart:updated', cart);
      return cart;
    });
  }
  Aori.refreshCart = refreshDrawer;
  Aori.getCart = function () { return getJSON('/cart.js'); };

  function renderDrawer(cart) {
    var body = document.querySelector('[data-cart-body]');
    if (!body) return;
    if (cart.item_count === 0) {
      body.innerHTML = '<div class="cart-drawer__empty">' + (strings.cartEmpty || 'Your cart is empty') + '</div>';
      emit('cart:refresh', cart);
      return;
    }
    var drawer = document.getElementById('CartDrawer');
    var showProgress = drawer && drawer.getAttribute('data-free-shipping') === 'true';
    var threshold = drawer ? parseInt(drawer.getAttribute('data-threshold'), 10) : 0;

    var items = cart.items.map(function (item, i) {
      var img = item.image
        ? '<img class="cart-line__img" src="' + item.image.replace(/(\.[^.]+)(\?.*)?$/, '_128x$1$2') + '" alt="" width="64" height="64" loading="lazy">'
        : '<span class="cart-line__img"></span>';
      return '' +
        '<li class="cart-line" data-line-key="' + item.key + '" data-line="' + (i + 1) + '">' +
          img +
          '<div class="cart-line__info">' +
            '<div class="cart-line__title"><a href="' + item.url + '">' + item.product_title + '</a></div>' +
            '<div class="cart-line__price">' + formatMoney(item.final_price) + '</div>' +
            '<div class="cart-line__qty">' +
              '<button type="button" data-qty-down aria-label="−">&minus;</button>' +
              '<span data-qty>' + item.quantity + '</span>' +
              '<button type="button" data-qty-up aria-label="+">+</button>' +
            '</div>' +
            '<button type="button" class="cart-line__remove" data-line-remove>' + (strings.remove || 'Remove') + '</button>' +
          '</div>' +
        '</li>';
    }).join('');

    var progress = '';
    if (showProgress && threshold > 0) {
      var remaining = threshold - cart.total_price;
      var pct = Math.min(100, (cart.total_price * 100) / threshold);
      progress =
        '<div class="cart-progress">' +
          (remaining > 0
            ? (strings.freeShippingProgress || "You're [amount] away from free shipping").replace('[amount]', formatMoney(remaining))
            : (strings.freeShipping || 'Free shipping unlocked!')) +
          '<div class="cart-progress__track"><div class="cart-progress__bar" style="width:' + pct + '%"></div></div>' +
        '</div>';
    }

    body.innerHTML =
      '<ul class="cart-drawer__items" data-cart-items>' + items + '</ul>' +
      '<div class="cart-drawer__foot">' +
        progress +
        '<div class="cart-drawer__totals"><span class="label">' + (strings.total || 'Total') + '</span>' +
          '<span class="value">' + formatMoney(cart.total_price) + '</span></div>' +
        '<a href="/checkout" class="cart-drawer__checkout">' + (strings.checkout || 'Checkout') + '</a>' +
        '<button type="button" class="cart-drawer__continue" data-drawer-close>' + (strings.continue || 'Continue Shopping') + '</button>' +
        '<p class="cart-drawer__note">' + (strings.taxesNote || '') + '</p>' +
      '</div>';

    // Drawer DOM was rebuilt — apps that inject pricing/UI should re-apply on this event.
    emit('cart:refresh', cart);
  }

  function addToCart(idOrForm, qty) {
    var body;
    if (idOrForm instanceof HTMLFormElement) {
      body = new FormData(idOrForm);
    } else {
      body = JSON.stringify({ items: [{ id: idOrForm, quantity: qty || 1 }] });
    }
    var opts = { method: 'POST', headers: { 'Accept': 'application/json' } };
    if (typeof body === 'string') { opts.headers['Content-Type'] = 'application/json'; opts.body = body; }
    else { opts.body = body; }

    return fetch(routes.cart_add_url || '/cart/add.js', opts)
      .then(function (r) { return r.json().then(function (d) { return { ok: r.ok, data: d }; }); })
      .then(function (res) {
        if (!res.ok) { throw new Error(res.data.description || strings.cartError); }
        return refreshDrawer().then(function (cart) { emit('cart:added', cart); openDrawer('cart'); return res.data; });
      });
  }
  Aori.addToCart = addToCart;

  function refreshCartPage() {
    // The full cart PAGE (/cart, sections/main-cart) isn't the drawer — re-render it
    // via the Section Rendering API so qty/remove reflect on screen (not just the badge).
    // No-op anywhere the cart page isn't present (e.g. the drawer on other pages).
    var current = document.querySelector('.cartpage[data-section-id]');
    if (!current) return Promise.resolve();
    var sid = current.getAttribute('data-section-id');
    return fetch((routes.cart_url || '/cart') + '?section_id=' + encodeURIComponent(sid))
      .then(function (r) { return r.text(); })
      .then(function (html) {
        var fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('.cartpage');
        var cur = document.querySelector('.cartpage');
        if (fresh && cur) cur.replaceWith(fresh);
      })
      .catch(function () { window.location.reload(); });
  }

  function changeLine(key, quantity) {
    return fetch(routes.cart_change_url || '/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: key, quantity: quantity })
    }).then(function (r) { return r.json(); }).then(function (cart) {
      updateCartCount(cart.item_count);
      renderDrawer(cart);
      refreshCartPage();
      emit('cart:updated', cart);
      return cart;
    });
  }

  /* Delegated cart interactions inside the drawer */
  document.addEventListener('click', function (e) {
    var line = e.target.closest('[data-line-key]');
    if (!line) return;
    var key = line.getAttribute('data-line-key');
    var qtyEl = line.querySelector('[data-qty]');
    var qty = qtyEl ? parseInt(qtyEl.textContent, 10) : 1;

    if (e.target.closest('[data-qty-up]')) { e.preventDefault(); changeLine(key, qty + 1); }
    else if (e.target.closest('[data-qty-down]')) { e.preventDefault(); changeLine(key, Math.max(0, qty - 1)); }
    else if (e.target.closest('[data-line-remove]')) { e.preventDefault(); changeLine(key, 0); }
  });

  /* Add-to-cart buttons / forms anywhere on the page */
  document.addEventListener('submit', function (e) {
    var form = e.target.closest('form[data-cart-form]');
    if (!form) return;
    e.preventDefault();
    var btn = form.querySelector('[type="submit"]');
    setBtnState(btn, 'adding');
    addToCart(form)
      .then(function () { setBtnState(btn, 'added'); })
      .catch(function (err) { setBtnState(btn, 'error', err.message); });
  });

  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-add-to-cart]');
    if (!btn) return;
    e.preventDefault();
    var id = btn.getAttribute('data-add-to-cart');
    if (!id) return;
    setBtnState(btn, 'adding');
    addToCart(id, 1)
      .then(function () { setBtnState(btn, 'added'); })
      .catch(function (err) { setBtnState(btn, 'error', err.message); });
  });

  function setBtnState(btn, state, msg) {
    if (!btn) return;
    if (!btn.dataset.label) btn.dataset.label = btn.getAttribute('aria-label') || btn.textContent.trim();
    btn.classList.remove('is-adding', 'is-added', 'is-error');
    if (state === 'adding') { btn.classList.add('is-adding'); btn.disabled = true; }
    else if (state === 'added') {
      btn.classList.add('is-added'); btn.disabled = false;
      setTimeout(function () { btn.classList.remove('is-added'); }, 1600);
    } else if (state === 'error') {
      btn.classList.add('is-error'); btn.disabled = false;
      if (msg) console.warn('[cart]', msg);
      setTimeout(function () { btn.classList.remove('is-error'); }, 2200);
    }
  }

  /* ---------- Reveal on scroll ------------------------------------------ */
  function initReveals() {
    var els = document.querySelectorAll('.reveal');
    if (!('IntersectionObserver' in window) || document.documentElement.classList.contains('reduced-motion')) {
      els.forEach(function (el) { el.classList.add('is-visible'); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) { entry.target.classList.add('is-visible'); io.unobserve(entry.target); }
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -8% 0px' });
    els.forEach(function (el) { io.observe(el); });
  }

  /* ---------- Scroll-aware header ---------------------------------------- */
  // Default header style is legible on the cream surface. Over a dark hero, add
  // 'header--over-hero' to restore the subtle white glass; remove once scrolled past.
  function initHeaderTheme() {
    var header = document.querySelector('[data-header]');
    if (!header) return;
    var hero = document.querySelector('.hero');
    if (!hero) { header.classList.remove('header--over-hero'); return; }

    var ticking = false;
    function update() {
      ticking = false;
      // When the horizontal stage is active it owns the header state — don't fight it.
      if (document.documentElement.classList.contains('hstage-on')) return;
      header.classList.toggle('header--over-hero', hero.getBoundingClientRect().bottom > 88);
    }
    function onScroll() { if (!ticking) { ticking = true; requestAnimationFrame(update); } }
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    update();
  }

  // On the landing, remember which section the customer left from
  function initOriginTracker() {
    if (initOriginTracker._done) return;
    var main = document.getElementById('MainContent');
    if (!main || main.dataset.template !== 'index') return;
    initOriginTracker._done = true;
    document.addEventListener('click', function (e) {
      var a = e.target.closest('a[href]');
      if (!a) return;
      var path;
      try { path = new URL(a.href, location.origin).pathname; } catch (err) { return; }
      if (!/^\/(products|collections)\//.test(path)) return;
      var sec = a.closest('section[id]');
      if (!sec || !sec.id) return;
      var h = sec.querySelector('.section-title, h2, h1');
      var label = (h ? h.textContent : '').replace(/\s+/g, ' ').trim().slice(0, 24);
      try { sessionStorage.setItem('aori:origin', JSON.stringify({ href: '/#' + sec.id, label: label })); } catch (err) {}
    }, true);
  }

  // Back control on inner pages: previous page + return to landing section of origin
  function initBackNav() {
    var nav = document.querySelector('[data-backnav]');
    if (!nav) return;
    var origin = null;
    try { origin = JSON.parse(sessionStorage.getItem('aori:origin') || 'null'); } catch (e) {}
    var home = nav.querySelector('[data-backnav-home]');
    if (origin && origin.href && home) {
      home.setAttribute('href', origin.href);
      var lbl = home.querySelector('.backnav__home-label');
      if (lbl && origin.label) lbl.textContent = 'Inicio · ' + origin.label;
    }
    var back = nav.querySelector('[data-back]');
    if (back && !back.dataset.bound) {
      back.dataset.bound = '1';
      back.addEventListener('click', function () {
        var ref = document.referrer, sameSite = false;
        try { sameSite = !!ref && new URL(ref).origin === location.origin; } catch (e) {}
        if (sameSite && history.length > 1) history.back();
        else if (origin && origin.href) location.href = origin.href;
        else location.href = '/';
      });
    }
  }

  function boot() { initReveals(); initHeaderTheme(); initOriginTracker(); initBackNav(); }

  if (document.readyState !== 'loading') boot();
  else document.addEventListener('DOMContentLoaded', boot);

  // Re-run when sections are re-rendered in the theme editor
  document.addEventListener('shopify:section:load', boot);
})();
